import React, { useState, useEffect } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Input } from 'reactstrap';
import { connect } from 'react-redux';
import { UserAction } from 'redux/actions';
import { MdRssFeed, MdContentCopy } from 'react-icons/md';

const RssFeedButton = ({ userId, generateRssFeed, getRssFeedInfo, rssFeedInfo, rssFeedGenerated }) => {
    const [modal, setModal] = useState(false);
    const [feedUrl, setFeedUrl] = useState('');
    const [copySuccess, setCopySuccess] = useState(false);
    const [currentFeedData, setCurrentFeedData] = useState(null);
    const [copiedUrl, setCopiedUrl] = useState(null);
    const [jsonLimit, setJsonLimit] = useState(10);
    const [xmlLimit, setXmlLimit] = useState(10);

    const toggle = () => setModal(!modal);

    useEffect(() => {
        if (modal && userId) {
            getRssFeedInfo({ user_id: userId });
        }
    }, [modal, userId, getRssFeedInfo]);

    useEffect(() => {
        if (rssFeedInfo) {
            setCurrentFeedData(rssFeedInfo);
            if (rssFeedInfo.has_feed && rssFeedInfo.feed_url) {
                setFeedUrl(rssFeedInfo.feed_url);
            }
        }
    }, [rssFeedInfo]);

    useEffect(() => {
        if (rssFeedGenerated && rssFeedGenerated.feed_url) {
            // Update the current feed data with the newly generated feed
            setCurrentFeedData({
                has_feed: true,
                feed_url: rssFeedGenerated.feed_url,
                feed_token: rssFeedGenerated.feed_token,
                is_enabled: rssFeedGenerated.is_enabled,
                created_at: rssFeedGenerated.created_at,
                access_count: rssFeedGenerated.access_count,
                last_accessed_at: rssFeedGenerated.last_accessed_at
            });
            setFeedUrl(rssFeedGenerated.feed_url);
        }
    }, [rssFeedGenerated]);

    const handleGenerateFeed = () => {
        generateRssFeed({ user_id: userId });
    };

    const copyToClipboard = async (url = feedUrl) => {
        try {
            await navigator.clipboard.writeText(url);
            setCopySuccess(true);
            setCopiedUrl(url);
            setTimeout(() => {
                setCopySuccess(false);
                setCopiedUrl(null);
            }, 2000);
        } catch (err) {
            console.error('Failed to copy: ', err);
        }
    };

    const hasFeed = currentFeedData && currentFeedData.has_feed;

    return (
        <>
            <Button
                color="primary"
                size="sm"
                style={{ marginTop: 10, width: '95%' }}
                onClick={toggle}
            >
                <MdRssFeed size={16} style={{ marginRight: 5 }} />
                RSS Feed
            </Button>

            <Modal isOpen={modal} toggle={toggle} size="lg">
                <ModalHeader toggle={toggle}>RSS Feed</ModalHeader>
                <ModalBody>
                    {!hasFeed ? (
                        <div>
                            <p>Generate an RSS feed for this user to access their posts via RSS readers, smart glasses, or other applications.</p>
                            <Button color="primary" onClick={handleGenerateFeed}>
                                Generate RSS Feed
                            </Button>
                        </div>
                    ) : (
                        <div>
                            <p>RSS feed is available for this user. You can copy the URL below:</p>

                            <div className="mt-3">
                                <h6>Feed Information:</h6>
                                {/* <p><strong>Status:</strong> {currentFeedData.is_enabled ? 'Enabled' : 'Disabled'}</p> */}
                                <p><strong>Created:</strong> {new Date(currentFeedData.created_at).toLocaleDateString()}</p>
                                <p><strong>Access Count:</strong> {currentFeedData.access_count}</p>
                                {currentFeedData.last_accessed_at && (
                                    <p><strong>Last Accessed:</strong> {new Date(currentFeedData.last_accessed_at).toLocaleDateString()}</p>
                                )}
                            </div>

                            <div className="mt-3">
                                <h6>Available Formats:</h6>
                                <div className="mb-3">
                                    <strong>JSON Format:</strong>
                                    <div style={{ display: 'flex', alignItems: 'center', marginTop: 5 }}>
                                        <Input
                                            type="text"
                                            value={`${feedUrl}?type=json&limit=${jsonLimit}`}
                                            readOnly
                                            style={{ marginRight: 10, flex: 1 }}
                                        />
                                        <Input
                                            type="select"
                                            value={jsonLimit}
                                            onChange={(e) => setJsonLimit(parseInt(e.target.value))}
                                            style={{ marginRight: 10, width: '80px' }}
                                            title="Select limit"
                                        >
                                            {[10, 15, 20, 25, 30, 35, 40, 45, 50].map(num => (
                                                <option key={num} value={num}>{num}</option>
                                            ))}
                                        </Input>
                                        <Button
                                            color="secondary"
                                            size="sm"
                                            onClick={() => copyToClipboard(`${feedUrl}?type=json&limit=${jsonLimit}`)}
                                            title="Copy JSON URL to clipboard"
                                        >
                                            <MdContentCopy size={16} />
                                        </Button>
                                    </div>
                                    {copiedUrl === `${feedUrl}?type=json&limit=${jsonLimit}` && copySuccess && (
                                        <small className="text-success">Copied to clipboard!</small>
                                    )}
                                </div>

                                <div className="mb-3">
                                    <strong>XML/RSS Format:</strong>
                                    <div style={{ display: 'flex', alignItems: 'center', marginTop: 5 }}>
                                        <Input
                                            type="text"
                                            value={`${feedUrl}?type=xml&limit=${xmlLimit}`}
                                            readOnly
                                            style={{ marginRight: 10, flex: 1 }}
                                        />
                                        <Input
                                            type="select"
                                            value={xmlLimit}
                                            onChange={(e) => setXmlLimit(parseInt(e.target.value))}
                                            style={{ marginRight: 10, width: '80px' }}
                                            title="Select limit"
                                        >
                                            {[10, 15, 20, 25, 30, 35, 40, 45, 50].map(num => (
                                                <option key={num} value={num}>{num}</option>
                                            ))}
                                        </Input>
                                        <Button
                                            color="secondary"
                                            size="sm"
                                            onClick={() => copyToClipboard(`${feedUrl}?type=xml&limit=${xmlLimit}`)}
                                            title="Copy XML URL to clipboard"
                                        >
                                            <MdContentCopy size={16} />
                                        </Button>
                                    </div>
                                    {copiedUrl === `${feedUrl}?type=xml&limit=${xmlLimit}` && copySuccess && (
                                        <small className="text-success">Copied to clipboard!</small>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggle}>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

const mapStateToProps = (state) => ({
    rssFeedInfo: (state.user && state.user.rssFeedInfo) ? state.user.rssFeedInfo : null,
    rssFeedGenerated: (state.user && state.user.rssFeedGenerated) ? state.user.rssFeedGenerated : null,
});

const mapDispatchToProps = (dispatch) => ({
    generateRssFeed: (payload) => dispatch(UserAction.generateRssFeed(payload)),
    getRssFeedInfo: (payload) => dispatch(UserAction.getRssFeedInfo(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(RssFeedButton);